/**
 * AddCustomModal.js
 * Created by calhosh on 10/19/2020.
 */

import React, {useState, useMemo, useEffect, useContext, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getTheme} from '@ey/styled-theme';
import styled, {ThemeProvider} from 'styled-components';
import {labels} from '../../../../util/utils';
import TextAreaEditorWrapper from '../../../Common/TextAreaEditor/TextAreaEditor';
import TabBar from '../../../Common/Tabs/TabBar';
import SelectDropdown from '../../../Common/SelectDropdown/SelectDropdown';
import env from '../../../../util/env';
import {serviceURL} from '../../../../util/uiconstants';
import {getUnresolvedCommentsCount} from '../../../../actions/ValidationControl/validationcontrolactions';
import {
	addCustom,
	addCustomBody,
	editSection,
	editHeader,
	editBody,
	getFormBodiesBySectionId
} from '../../../../actions/FormData/formdataactions';
import {getTaskDocumentByDocumentId} from '../../../../selectors/Ares/selectors';
import LoadingIndicator from '../../../Common/LoadingIndicator/LoadingIndicator';
import EllipseControl from '../../../Common/EllipsesControl/EllipsesControl';
import {FormBodyTypeIdContext} from '../../../Common/CanvasForm/Bodies/FormBodyContainer';
import Modal from '../../../Common/Modal/Modal';
import {setIsModalOpen} from '../../../../actions/setismodalopen';
import utility from '../../../../util/utilityfunctions';
import GrayFade from '../../../Common/GrayFade/GrayFade';
import {expandableGetFormById, useIsAresView} from '../../../../util/Ares/customHooks';
import {useEngagement} from '../../../../util/customHooks';

export default function AddEditCustomModal(props) {
	const {
		afterSave,
		documentId,
		engagementId,
		entity = 'header',
		entityId,
		isEdit,
		isCustom,
		showTabs = true,
		onHide,
		formData,
		entityValue,
		entityCustomLabel,
		entityWithNoCustomLabel,
		entityParentId,
		entityImmediateParentId
	} = props;

	const isAresView = useIsAresView();
	const engagement = useEngagement();

	const currentDocument = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));

	const documentTypeId = currentDocument?.documentTypeId;
	const embeddedBodyTypeIds = currentDocument?.embeddedBodyTypeIds;

	const [activeTab, setActiveTab] = useState(entity),
		[sectionError, setSectionError] = useState(''),
		[error, setError] = useState(''),
		[showSectionHeaderError, setShowSectionHeaderError] = useState(false),
		[sectionHeaderErrorMessage, setSectionHeaderErrorMessage] = useState(''),
		[name, setName] = useState(entityValue || ''),
		[saving, setSaving] = useState(false),
		[show, setShow] = useState(true),
		[customLabel, setCustomLabel] = useState(entityCustomLabel || ''),
		[currentBodyHeader, setCurrentBodyHeader] = useState([]),
		[bodyHeaderList, setBodyHeaderList] = useState([]),
		[bodySectionList, setBodySectionList] = useState([]),
		[currentBodySection, setCurrentBodySection] = useState([]),
		[bodyDescription, setBodyDescription] = useState(entityValue || ''),
		[bodyDescriptionError, setBodyDescriptionError] = useState(''),
		[bodyHeaderError, setBodyHeaderError] = useState(''),
		[bodySectionError, setBodySectionError] = useState(''),
		[customSectionName, setCustomSectionName] = useState(entityWithNoCustomLabel || ''),
		[sectionHeaderList, setSectionHeaderList] = useState([]),
		[currentSectionHeader, setCurrentSectionHeader] = useState([]);

	const theme = utility.getTheme();

	const formBodyTypeId = useContext(FormBodyTypeIdContext);
	const dispatch = useDispatch();

	let tabs = [];
	if (!isEdit) {
		tabs.push({id: 'header', label: labels.header});
		tabs.push({id: 'section', label: labels.section});
		tabs.push({id: 'body', label: labels.body});
	}

	const getValidations = useCallback(() => {
		return dispatch(
			getUnresolvedCommentsCount(engagementId, documentId, documentTypeId, undefined, embeddedBodyTypeIds)
		);
	}, [dispatch, engagementId, documentId, documentTypeId, embeddedBodyTypeIds]);

	const changeCurrentTab = (tab) => {
		setActiveTab(tab);
		if (tab === tabs[1].id) {
			if (sectionHeaderList && sectionHeaderList.length === 1) {
				setCurrentSectionHeader(sectionHeaderList);
			}
		}
		if (currentBodyHeader && currentBodyHeader.length < 1 && tab === tabs[2].id) {
			if (bodyHeaderList && bodyHeaderList.length === 1) {
				setCurrentBodyHeader(bodyHeaderList);
				getSectionForHeader(bodyHeaderList);
			}
		}
	};

	const changeName = (e) => {
		setName(e.currentTarget.value);
	};

	const onCustomSectionNameChange = (e) => {
		setCustomSectionName(e.currentTarget.value);
		setSectionError('');
	};

	const onBodyDescriptionTextChange = (e) => {
		setBodyDescriptionError('');
		setBodyDescription(e.currentTarget.value);
	};

	const onBodySectionChange = (option) => {
		const currentBodySection = bodySectionList.filter((fl) => fl.value === option.value);
		setCurrentBodySection(currentBodySection);
		setBodySectionError('');
	};

	const onBodyHeaderChange = (option) => {
		const currentBodyHeader = bodyHeaderList.filter((fl) => fl.value === option.value);
		setCurrentBodyHeader(currentBodyHeader);
		setBodySectionList([]);
		setCurrentBodySection([]);
		setBodyHeaderError('');
		setBodySectionError('');
		getSectionForHeader(currentBodyHeader);
	};

	const setHeaderData = () => {
		let headerList = [];
		if (formData && formData.headers && formData.headers.length > 0) {
			headerList = formData.headers
				.map((item, i) => {
					if (!item?.sections?.map((x) => x.isSummary)?.includes(true)) {
						return {
							value: item.id,
							label: item.name,
							title: item.name,
							isCustom: item.isCustom,
							knowledgeDisplayOrder: item.knowledgeDisplayOrder
						};
					}
				})
				.filter((x) => x !== undefined);
		}

		utility.sortAlphaNumeric(headerList, 'label');
		setSectionHeaderList(headerList);
		setBodyHeaderList(headerList);
		if (headerList && headerList.length <= 1 && entityParentId === null) {
			//default select header if only 1 header and default select section if only one.
			setCurrentBodyHeader(headerList);
			getSectionForHeader(headerList);
		} else if (entityParentId) {
			let parent = headerList.find((h) => {
				return h.value === entityParentId;
			});
			if (parent) {
				setCurrentSectionHeader([parent]);
				setCurrentBodyHeader([parent]);
				getSectionForHeader([parent]);
			}
		}
	};

	const getSectionForHeader = (header) => {
		const associatedHeader = formData.headers.filter((fl) => {
			if (fl.id === header[0].value) {
				return fl.sections;
			}
		});

		let sectionList = [];
		if (associatedHeader && associatedHeader.length > 0 && associatedHeader[0].sections) {
			sectionList = associatedHeader[0].sections
				.map((item) => {
					if (item?.isCustom || item?.isDisplayed) {
						return {
							value: item.id,
							label: item.name,
							title: item.name,
							isCustom: item.isCustom,
							knowledgeDisplayOrder: item.knowledgeDisplayOrder,
							customLabel: item.customLabel
						};
					}
				}, this)
				.filter((x) => x !== undefined);
			utility.sortAlphaNumeric(sectionList, 'label');
			setBodySectionList(sectionList);
			if (sectionList && sectionList.length <= 1) {
				//default select section if only one.
				setCurrentBodySection(sectionList);
				setBodySectionError('');
			} else if (entityImmediateParentId) {
				let parent = sectionList.find((h) => {
					return h.value === entityImmediateParentId;
				});
				if (parent) {
					setCurrentBodySection([parent]);
				}
			}
		}
	};

	const onHeaderChange = (option) => {
		const currentHeader = sectionHeaderList.filter((fl) => fl.value === option.value);
		setCurrentSectionHeader(currentHeader);
		setShowSectionHeaderError(false);
	};

	const headerOptionLabel = ({label}) => (
		<section className="selectText">
			<EllipseControl
				tooltipClass="tooltipCustomWidth"
				content={label}
				id={label}
				tooltip={label}
				displayId={''}
				noOfLines={1}
			/>
		</section>
	);

	const bodySectionOptionLabel = ({label, customLabel}) => {
		let sectionOption = customLabel && customLabel.length > 0 ? label + ' - ' + customLabel : label;
		return (
			<section className="selectText">
				<EllipseControl
					tooltipClass="tooltipCustomWidth"
					content={sectionOption}
					id={sectionOption}
					tooltip={sectionOption}
					displayId={''}
					noOfLines={1}
				/>
			</section>
		);
	};

	useEffect(() => {
		setHeaderData();
		dispatch(setIsModalOpen(true));
	}, []);

	const createCustomCanvasForm = useMemo(() => {
		switch (activeTab) {
			case 'header':
				return (
					<section className="customTextEditor customScrollbar">
						<TextAreaEditorWrapper
							//placeholder={labels.headerTitle}
							maxLength={500}
							showCounter
							isDisabled={isEdit && !isCustom}
							value={name}
							onChange={changeName}
							textAreaError={error.length > 0}
							textAreaErrorMessage={error}
							textAreaEditorLabel={labels.headerTitle}
						/>
					</section>
				);
			case 'section':
				return (
					<section className="customTextEditor customScrollbar">
						<section className="customHeader">
							<StyledHeaderSelectForSection
								formLabel={labels.header}
								className={`HeaderSelect ${
									currentSectionHeader.length === 0 && sectionHeaderList.length > 1
										? 'LabelCenter'
										: 'noOptionsAvailable'
								}`}
								value={
									sectionHeaderList && sectionHeaderList.length === 1 ? sectionHeaderList[0] : currentSectionHeader[0]
								}
								id="HeaderNamesForSection"
								noOptionsMessage={() => labels.noResultsFound}
								formatOptionLabel={headerOptionLabel}
								isClearable={false}
								defaultValue={
									sectionHeaderList && sectionHeaderList.length > 1 ? currentSectionHeader : labels.noRecordsAvailable
								}
								options={sectionHeaderList}
								onChange={onHeaderChange}
								isDisabled={isEdit ? isCustom === false : sectionHeaderList.length === 0}
								errorMessage={sectionHeaderErrorMessage}
								showErrorMessage={showSectionHeaderError}
								placeholder={
									currentSectionHeader &&
									currentSectionHeader.length === 0 &&
									sectionHeaderList &&
									sectionHeaderList.length === 0 &&
									labels.noRecordsAvailable
								}
								isAresErrorIcon={false}
							/>
						</section>
						<section className="customSectionTitle">
							<TextAreaEditorWrapper
								key={documentId + 'sectionTitle'}
								maxLength={500}
								showCounter
								isDisabled={isEdit && !isCustom}
								value={customSectionName}
								onChange={onCustomSectionNameChange}
								textAreaError={sectionError.length > 0}
								textAreaErrorMessage={sectionError}
								textAreaEditorLabel={isAresView ? labels.aresSectionTitle : labels.sectionTitle}
								labelPosition="in"
							/>
						</section>
						<section className="customSectionLabel">
							<TextAreaEditorWrapper
								key={documentId + 'sectionLabel'}
								maxLength={100}
								showCounter
								value={customLabel}
								onChange={(e) => {
									setCustomLabel(e.currentTarget.value);
								}}
								textAreaEditorLabel={labels.customLabel}
								isAresOutlineErrorIcon
								labelPosition="in"
							/>
						</section>
					</section>
				);
			case 'body':
				return (
					<section className="customTextEditor customScrollbar">
						<section className="customHeader">
							<StyledHeaderSelect
								formLabel={labels.header}
								className={`HeaderBodySelect ${
									currentBodyHeader.length === 0 && bodyHeaderList.length > 1 ? 'LabelCenter' : 'noOptionsAvailable'
								}`}
								defaultValue={currentBodyHeader[0]}
								value={bodyHeaderList && bodyHeaderList.length === 1 ? bodyHeaderList[0] : currentBodyHeader[0]}
								id="HeaderNameForBody"
								noOptionsMessage={() => labels.noResultsFound}
								formatOptionLabel={headerOptionLabel}
								isClearable={false}
								options={bodyHeaderList}
								onChange={onBodyHeaderChange}
								isDisabled={isEdit ? isCustom === false : bodyHeaderList.length === 0}
								placeholder={
									currentBodyHeader &&
									currentBodyHeader.length === 0 &&
									bodyHeaderList &&
									bodyHeaderList.length === 0 &&
									labels.noRecordsAvailable
								}
								showErrorMessage={bodyHeaderError.length > 0}
								errorMessage={bodyHeaderError}
							/>
						</section>
						<section className="customHeader customSection">
							<StyledHeaderSelect
								formLabel={labels.section}
								className={`SectionBodySelect ${
									currentBodySection.length === 0 && bodySectionList.length > 1 ? 'LabelCenter' : 'noOptionsAvailable'
								}`}
								defaultValue={currentBodySection[0]}
								value={
									bodySectionList && bodySectionList.length === 1
										? bodySectionList[0]
										: bodySectionList.length === 0
										? []
										: currentBodySection
								}
								id="SectionNameForBody"
								noOptionsMessage={() => labels.noResultsFound}
								formatOptionLabel={bodySectionOptionLabel}
								isClearable={false}
								options={bodySectionList}
								onChange={onBodySectionChange}
								isDisabled={isEdit ? isCustom === false : bodySectionList.length === 0}
								placeholder={
									currentBodySection &&
									currentBodySection.length === 0 &&
									bodySectionList &&
									bodySectionList.length === 0 &&
									labels.noRecordsAvailable
								}
								showErrorMessage={bodySectionError.length > 0}
								errorMessage={bodySectionError}
							/>
						</section>
						<section className="customBodyInput">
							<TextAreaEditorWrapper
								key={documentId + 'bodyInput'}
								maxLength={500}
								showCounter
								value={bodyDescription}
								onChange={onBodyDescriptionTextChange}
								textAreaError={bodyDescriptionError.length > 0}
								textAreaErrorMessage={bodyDescriptionError}
								textAreaEditorLabel={labels.customBodyDescription}
								isAresOutlineErrorIcon
							/>
						</section>
					</section>
				);
		}
	});

	const saveCustom = () => {
		let promise = Promise.resolve();

		setSaving(true);
		if (showTabs === true) {
			switch (activeTab) {
				case 'header':
					if (!name || name.trim().length === 0) {
						setSaving(false);
						setName(undefined);
						setError(labels.headerTitleRequired);
					} else {
						promise = dispatch(
							addCustom(env.getBaseURL(serviceURL, Number(engagementId)) + 'forms/' + documentId + '/headers', {
								formHeaderName: name,
								documentId: documentId,
								type: activeTab
							})
						);

						promise = promise.then(() =>
							dispatch(expandableGetFormById(engagement, documentId, undefined, undefined, undefined, true))
						);

						promise = promise
							.then(() => {
								setShow(false);
								afterSave();
							})
							.finally(() => {
								setSaving(false);
							});
					}

					return promise;
				case 'section':
					if (
						!customSectionName ||
						customSectionName.trim().length === 0 ||
						(currentSectionHeader && currentSectionHeader.length < 1)
					) {
						setSaving(false);
						if (!customSectionName || customSectionName.trim().length === 0) {
							setCustomSectionName(undefined);
							setSectionError(labels.sectionTitleRequired);
						}
						if (currentSectionHeader && currentSectionHeader.length < 1) {
							setSectionHeaderErrorMessage(labels.headerRequiredMessage);
							setShowSectionHeaderError(true);
						}
					} else {
						promise = dispatch(
							addCustom(
								env.getBaseURL(serviceURL, Number(engagementId)) +
									'forms/' +
									documentId +
									'/headers/' +
									currentSectionHeader[0].value +
									'/sections',
								{
									formSectionName: customSectionName,
									customLabel: customLabel,
									documentId: documentId,
									type: activeTab,
									headerId: currentSectionHeader[0].value
								}
							)
						);

						promise = promise.then(() =>
							dispatch(expandableGetFormById(engagement, documentId, undefined, undefined, undefined, true))
						);

						promise = promise
							.then(() => {
								setShow(false);
								afterSave(currentSectionHeader[0].value);
							})
							.finally(() => {
								setSaving(false);
							});
					}
					break;
				case 'body':
					if (currentBodyHeader && currentBodyHeader.length < 1) {
						setSaving(false);
						setBodyHeaderError(labels.bodyHeaderRequired);
					}
					if (currentBodySection && currentBodySection.length < 1) {
						setSaving(false);
						setBodySectionError(isAresView ? labels.required : labels.bodySectionRequired);
					}
					if (!bodyDescription || bodyDescription.trim().length === 0) {
						setSaving(false);
						setBodyDescription(undefined);
						setBodyDescriptionError(isAresView ? labels.required : labels.bodyDescriptionRequired);
					} else {
						promise = dispatch(
							addCustomBody(
								env.getBaseURL(serviceURL, Number(engagementId)) +
									'forms/' +
									documentId +
									'/headers/' +
									currentBodyHeader[0].value +
									'/sections/' +
									currentBodySection[0].value +
									'/bodies',
								{
									formBodyDescription: bodyDescription,
									documentId: documentId,
									type: activeTab,
									headerId: currentBodyHeader[0].value,
									sectionId: currentBodySection[0].value
								}
							)
						);

						const bodyHeaderId = currentBodyHeader[0].value;
						const bodySectionId = currentBodySection[0].value;

						promise = Promise.all([
							getValidations(),
							dispatch(
								getFormBodiesBySectionId(
									env.getBaseURL(serviceURL, engagementId) +
										`forms/${documentId}/headers/${bodyHeaderId}/sections/${bodySectionId}/Bodies?includeAction=true`
								)
							)
						]);

						promise = promise
							.then(() => {
								setShow(false);
								afterSave(currentBodyHeader[0].value);
							})
							.finally(() => {
								setSaving(false);
							});
					}
					break;
			}
		} else if (isEdit) {
			switch (entity) {
				case 'header':
					if (!name || name.trim().length === 0) {
						setSaving(false);
						setName(undefined);
						setError(labels.headerTitleRequired);
					} else {
						promise = dispatch(
							editHeader(env.getBaseURL(serviceURL, Number(engagementId)) + `forms/${documentId}/headers/${entityId}`, {
								formHeaderName: name,
								documentId: documentId,
								headerId: entityId,
								type: entity
							})
						);

						promise = promise
							.then(() => {
								setShow(false);
								afterSave();
							})
							.finally(() => {
								setSaving(false);
							});
					}
					break;
				case 'section':
					if (!customSectionName || customSectionName.trim().length === 0) {
						setSaving(false);
						if (!customSectionName || customSectionName.trim().length === 0) {
							setCustomSectionName(undefined);
							setSectionError(isAresView ? labels.required : labels.sectionTitleRequired);
						}
					} else {
						promise = dispatch(
							editSection(
								env.getBaseURL(serviceURL, Number(engagementId)) + `forms/${documentId}/sections/${entityId}`,
								{
									formSectionName: customSectionName.trim(),
									customLabel: customLabel.trim(),
									headerId: currentSectionHeader[0].value,
									formHeaderId: currentSectionHeader[0].value,
									documentId: documentId,
									type: entity
								}
							)
						);

						promise = promise
							.then(() => {
								setShow(false);
								afterSave(currentSectionHeader[0].value);
							})
							.finally(() => {
								setSaving(false);
							});
					}
					break;
				case 'body':
					if (currentBodyHeader && currentBodyHeader.length < 1) {
						setSaving(false);
						setBodyHeaderError(labels.bodyHeaderRequired);
					}
					if (currentBodySection && currentBodySection.length < 1) {
						setSaving(false);
						setBodySectionError(isAresView ? labels.required : labels.bodySectionRequired);
					}
					if (!bodyDescription || bodyDescription.trim().length === 0) {
						setSaving(false);
						setBodyDescription(undefined);
						setBodyDescriptionError(isAresView ? labels.required : labels.bodyDescriptionRequired);
					} else {
						promise = dispatch(
							editBody(env.getBaseURL(serviceURL, Number(engagementId)) + `forms/${documentId}/bodies/${entityId}`, {
								bodyTypeId: formBodyTypeId,
								bodyResponseObject: [],
								sectionId: currentBodySection[0].value,
								formBodyDescription: bodyDescription,
								documentId: documentId,
								type: entity
							})
						);

						promise = promise
							.then(() => {
								setShow(false);
								afterSave(currentBodyHeader[0].value);
							})
							.finally(() => {
								setSaving(false);
							});
					}
					break;
			}
		}
	};

	const setModalTitle = () => {
		if (!isEdit) {
			return labels.createCustom;
		}
		switch (entity) {
			case 'header':
				return labels.editHeader;
			case 'section':
				return labels.editSection;
			case 'body':
				return labels.editBody;
		}
	};

	const setBodyDescriptionText = () => {
		if (!isEdit) {
			return (
				<section className="customHeading">
					<EllipseControl
						content={isAresView ? labels.addAresCustomDescription : labels.addCustomDescription}
						id={''}
						tooltip={isAresView ? labels.addAresCustomDescription : labels.addCustomDescription}
						displayId={''}
						noOfLines={1}
					/>
				</section>
			);
		}
		switch (entity) {
			case 'header':
				return (
					<section className="customHeading">
						<EllipseControl
							content={labels.editHeaderDescription}
							id={''}
							tooltip={labels.editHeaderDescription}
							displayId={''}
							noOfLines={1}
						/>
					</section>
				);
			case 'section':
				return (
					<section className="customHeading">
						<EllipseControl
							content={labels.editSectionDescription}
							id={''}
							tooltip={labels.editSectionDescription}
							displayId={''}
							noOfLines={1}
						/>
					</section>
				);
			case 'body':
				return (
					<section className="customHeading">
						<EllipseControl
							content={labels.editBodyDescription}
							id={''}
							tooltip={labels.editBodyDescription}
							displayId={''}
							noOfLines={1}
						/>
					</section>
				);
		}
	};

	return (
		<ThemeProvider theme={getTheme(theme)}>
			<Modal
				show={show}
				showButtons
				title={setModalTitle()}
				closeTitle={labels.closeLabel}
				onHide={onHide}
				closeBtnTitle={labels.cancelButtonLabel}
				onOkClick={saveCustom}
				confirmBtnTitle={labels.saveLabel}
				isOkButtonDisabled={saving}
				isCancelButtonDisabled={saving}
				modalContainerClass="AddEditCustomModalContainer"
				modalbodystyle="AddEditCustomModalBody"
			>
				<StyledAddEditCustomModalBody className="StyledAddEditCustomModalBody">
					<GrayFade isGrayFade={saving}>
						{saving && <LoadingIndicator view="blockView" show={saving} styleName="add-custom-loader" />}
						{setBodyDescriptionText()}
						{showTabs && (
							<TabBar
								styleName={'CustomTabBar'}
								icon
								badge
								activeTab={activeTab}
								tabs={tabs}
								onTabChange={changeCurrentTab}
							/>
						)}
						<>{activeTab && createCustomCanvasForm}</>
					</GrayFade>
				</StyledAddEditCustomModalBody>
			</Modal>
		</ThemeProvider>
	);
}

const StyledAddEditCustomModalBody = styled.section`
	height: 100%;
	display: flex;
	flex-direction: column;
	position: relative;
	.gray-fade {
		height: calc(100% - var(--px-50));
		.add-custom-loader {
			position: absolute;
			z-index: 10;
			width: 100%;
			height: 100%;
			display: flex;
			justify-content: center;
			align-items: center;
		}
		.customHeading {
			font-size: var(--px-14);
			font-weight: 300;
			color: var(--neutrals-900);
			margin-bottom: var(--px-24);
			padding-bottom: var(--px-24);
			border-bottom: 1px solid var(--neutrals-100);
		}
		.CustomTabBar {
			height: var(--px-45);
			padding-top: 0;
			flex: 0;
			margin-bottom: var(--px-24);
			.motif-tab-navigation {
				width: calc(100% - var(--px-20));
			}
		}
		.customTextEditor {
			width: 100%;
			height: calc(100% - var(--px-70));
			.pull-right-counter {
				margin-left: auto;
			}
			textarea {
				min-height: var(--px-90);
				overflow-y: auto;
				&::-webkit-scrollbar {
					height: var(--px-6);
					width: var(--px-6);
				}
				&::-webkit-scrollbar-track {
					background: var(--neutrals-200);
					height: var(--px-6);
					width: var(--px-6);
					border-radius: 0 var(--px-8) var(--px-8) 0;
					margin-bottom: var(--px-2);
				}
				&::-webkit-scrollbar-thumb {
					background: var(--neutrals-300);
					height: var(--px-6);
					width: var(--px-6);
					cursor: pointer;
					border-radius: 0 var(--px-8) var(--px-8) 0;
				}
				&::-webkit-scrollbar-thumb:hover {
					background: var(--neutrals-700);
				}
			}
			.customHeader {
				margin-bottom: var(--px-24);
				.SelectDropdownContainer {
					.react-select-container {
						.react-select__menu {
							margin-left:  calc(var(--px-1) * -1) !important;
							width: calc(100% + var(--px-2)) !important;
						}
					}
				}
			}
			.customSectionTitle,
			.customSectionLabel,
			.customBodyInput {
				margin-bottom: var(--px-24);
			}
			.customSectionTitle {
				.ErrorTextAreaEditor {
					.text-area-editor {
						.motif-error-message {
							margin-top: 0;
							.motif-error-icon {
								display: flex;
								align-items: center;
								svg {
									width: var(--px-18) !important;
									height: var(--px-18) !important;
								}
							}
						}
					}
				}
			}
			.customSection {
				.select - disabled {
					margin-top: var(--px-15);
				}
			}
			.customSectionLabel {
				textarea {
					min-height: var(--px-50);
				}
			}
		}
	}
`;

const StyledHeaderSelect = styled(SelectDropdown)`
	display: block;
	label: empty {
		display: none;
	}
	&.select-disabled {
		label {
			color: var(--neutrals-300);
		}
	}
	&.HeaderBodySelect,
	&.SectionBodySelect {
		.react-select-container {
			display: block;
			width: 100%;
			.react-select__control {
				.react-select__single-value {
					display: flex;
					margin-left: calc(var(--px-2) * -1);
					.selectText {
						width: 100%;
						margin: calc(var(--px-3) * -1) 0 0 0;
						@media only screen and(max-width: 1280px) {
							margin: var(--px-2) 0 0 0;
						}
					}
				}
			}
			.react-select__menu {
				.react-select__menu-list {
					max-height: var(--px-250);
				}
			}
		}
		&.noOptionsAvailable {
			label {
				top: var(--px-5);
			}
			.react-select__placeholder {
				top: var(--px-25);
			}
		}
	}
	&.select-error {
		.show {
			.medium {
				@media only screen and(max-width: 1280px) {
					margin-top: var(--px-6);
				}
			}
		}
	}
	.selectOption {
		display: flex;
		width: 100%;
		align-items: center;
		.selectText {
			display: inline - block;
			text-overflow: ellipsis;
			width: calc(100% - var(--px-76));
			overflow: hidden;
			margin-right: var(--px-10);
		}
		.doneInOptions {
			fill: var(--green-200);
			margin-left: auto;
		}
	}
`;

const StyledHeaderSelectForSection = styled(SelectDropdown)`
	display: block;
	label: empty {
		display: none;
	}
	&.select-disabled {
		label {
			color: var(--neutrals-300);
		}
	}
	&.HeaderSelect {
		.react-select-container {
			display: block;
			width: 100%;
			.react-select__control {
				.react-select__single-value {
					display: flex;
					margin-left: calc(var(--px-2) * -1);
					.selectText {
						width: 100%;
						margin: calc(var(--px-3) * -1) 0 0 0;
						@media only screen and(max-width: 1280px) {
							margin: var(--px-2) 0 0 0;
						}
					}
				}
			}
			.react-select__menu {
				.react-select__menu-list {
					max-height: var(--px-250);
				}
			}
		}
		&.noOptionsAvailable {
			label {
				top: var(--px-5);
			}
			.react-select__placeholder {
				top: var(--px-25);
			}
		}
	}
	&.select-error {
		.show {
			.medium {
				@media only screen and(max-width: 1280px) {
					margin-top: var(--px-6);
				}
			}
		}
	}
	.selectOption {
		display: flex;
		width: 100%;
		align-items: center;
		.selectText {
			display: inline - block;
			text-overflow: ellipsis;
			width: calc(100% - var(--px-76));
			overflow: hidden;
			margin-right: var(--px-10);
		}
		.doneInOptions {
			fill: var(--green-200);
			margin-left: auto;
		}
	}
`;
